/*
  Quick add — sections/quick-add.liquid
  ------------------------------------
  Opens the product card's "+" into a sheet from the right instead of navigating.

  It listens for `quick-shop:open`, the cancelable event the cards already dispatch
  (`_lifestyle-product-card` and `_mini-product-block` fire the same one), so nothing
  in the card had to change: cancelling the event is what turns the link into a
  drawer, and with this file blocked the anchor navigates to the product page exactly
  as before.

  The sheet's contents come from Section Rendering against the product's own URL,
  which is what puts the right product in scope without a bespoke endpoint.

  Four jobs once it is open:

    • Media track. Arrows, disabled states and the progress bar.
    • Variant selection. Resolves the radios against the rendered variant JSON, then
      updates the hidden id, the price, the button, the image and the details link.
      The sheet opens on a colour and no size — the card's colour, else the first with
      stock, which `_pdp-variant-picker` checks itself — so the gallery sits on that
      colour's image and the button reads "Choose a size" until a size is picked. A
      size is checked on arrival only when the request named it: a sibling switch
      carrying the sizes chosen here as `option_values`, or a `?variant=` link. A
      combined listing's sibling colour is a link to another product, so it re-fetches
      the sheet from that link with the current contents left in place.
    • Add to cart, through `window.themeCart` so the sheet stays open and the cart
      state the withheld-option resolver reads carries the add.
    • Save for later, which broadcasts the theme-wide `wishlist:toggle` and lets
      `assets/wishlist.js` save it.
*/
(function () {
  if (window.__quickAdd) return;
  window.__quickAdd = true;

  /* The file name only ever renders a default copy. The drawer publishes the id of the
     instance in the header group, which is the one carrying the merchant's settings and any
     app block, so that is what gets re-fetched. Falls back to the file name if it is absent. */
  var SECTION = 'quick-add';

  /* Editor only. The shell renders in design mode and holds the stars block's placeholder
     row; kept here because the first fetch replaces the body it lives in. */
  var placeholderRow = null;

  function sectionId() {
    var d = drawer();
    return (d && d.getAttribute('data-section-id')) || SECTION;
  }

  /* One sequence over every open, because a second product opened while the first is
     still loading must win. Same shape as assets/product-grid.js. `closeTimer` is
     superseded too: a pending close from the previous product would otherwise shut the
     sheet that just replaced it. */
  var seq = 0;
  var inFlight = null;
  var closeTimer = null;

  function supersede() {
    if (inFlight) inFlight.abort();
    inFlight = null;
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    return ++seq;
  }

  function drawer() {
    return document.querySelector('[data-quick-add-drawer]');
  }

  function body() {
    var d = drawer();
    return d ? d.querySelector('[data-drawer-body]') : null;
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function root() {
    return window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  }

  /* The header count is server-rendered and there is no cart controller to own it, so
     the elements that show it are updated here from the authoritative cart. Marked up
     with `data-cart-count`; the header already carries it. */
  function updateCartCount(count) {
    if (count == null) return;
    var nodes = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = count;
      nodes[i].toggleAttribute('hidden', count === 0);
    }
  }

  /* ---------- Loading ---------- */

  /* The media block leads the sheet, so the first control in DOM order is the previous
     image arrow — disabled at the start, and display:none below 750px. Focusing it put
     keyboard and screen reader users on a control that is not there, so visibility is
     checked rather than position trusted. `[data-drawer-autofocus]` wins if a block
     ever marks one, which is the hook assets/drawer.js already uses. */
  function focusFirst(host) {
    var marked = host.querySelector('[data-drawer-autofocus]');
    if (marked) return marked.focus({ preventScroll: true });

    var candidates = host.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])'
    );
    for (var i = 0; i < candidates.length; i++) {
      // offsetParent is null for anything display:none, itself or via an ancestor.
      if (candidates[i].offsetParent !== null) {
        return candidates[i].focus({ preventScroll: true });
      }
    }
  }

  /* `opts.keep` leaves the current sheet on screen, dimmed, until the next one lands: a
     colour switch inside the sheet is the same product to the shopper, and a skeleton
     between two colours reads as the sheet closing. `opts.values` are the sizes it had
     chosen, checked again in the new markup; `opts.focus` is where focus goes once it
     has landed. */
  function load(url, trigger, opts) {
    var host = body();
    var d = drawer();
    if (!host || !d) return;
    opts = opts || {};

    /* Before the skeleton, which replaces the whole body: the shell rendered in design mode
       and is the only copy of the stars block's placeholder row. */
    if (window.Shopify && window.Shopify.designMode) {
      var shellRow = host.querySelector('[data-rating-placeholder]');
      if (shellRow) placeholderRow = shellRow.cloneNode(true);
    }

    var mine = supersede();
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    inFlight = controller;

    /* A skeleton of the real sheet, not the word "Loading".

       It is also what makes the open read properly. The panel slides in over 400ms
       while the fetch is still in flight, so with a one-line message the sheet arrived
       nearly empty and then everything appeared at once, mid-slide -- which is the
       lurch that kept reading as a bad animation. A skeleton the same shape and height
       as the real content means the panel slides in already full, and the swap when
       the markup lands changes only the contents of boxes that were already there.

       Mirrors the block order of sections/quick-add.liquid: media, title, price,
       swatch row, size grid, add to cart. `aria-hidden` because the live region below
       is what announces the state; the boxes themselves are decorative. */
    var current = host.querySelector('[data-quick-add]');
    var keep = !!(opts.keep && current);
    if (keep) {
      current.setAttribute('aria-busy', 'true');
    } else {
      host.innerHTML =
        '<div class="quick-add quick-add--skeleton" aria-hidden="true">' +
          '<div class="qa-skel qa-skel__media"></div>' +
          '<div class="qa-skel qa-skel__bar qa-skel__bar--title"></div>' +
          '<div class="qa-skel qa-skel__bar qa-skel__bar--price"></div>' +
          '<div class="qa-skel__row">' +
            '<div class="qa-skel qa-skel__swatch"></div>'.repeat(4) +
          '</div>' +
          '<div class="qa-skel__grid">' +
            '<div class="qa-skel qa-skel__size"></div>'.repeat(6) +
          '</div>' +
          '<div class="qa-skel qa-skel__cta"></div>' +
        '</div>' +
        '<p class="visually-hidden" role="status">' +
          (d.getAttribute('data-loading-label') || '') +
        '</p>';
    }

    // Open first, fetch second: the sheet animating in while the content arrives is
    // what makes it feel instant. The alternative is a dead click for ~300ms.
    if (!d.open) {
      d.__drawerReturnFocus = trigger || null;
      /* Non-modal in the editor: showModal() makes the rest of the preview inert, so the
         merchant could no longer click another section to move on. */
      if (window.Shopify && window.Shopify.designMode) {
        d.show();
      } else if (typeof d.showModal === 'function') {
        d.showModal();
      }
    }

    var parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('section_id', sectionId());

    fetch(parsed.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: controller && controller.signal,
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Quick add request failed: ' + r.status);
        return r.text();
      })
      .then(function (html) {
        // A newer product opened while this was in flight; its markup owns the sheet.
        if (mine !== seq) return;
        inFlight = null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('[data-quick-add]');
        if (!content) throw new Error('Quick add response had no content');

        /* Editor only. The shell rendered in design mode and so holds the stars block's
           placeholder row; the fetch is a plain storefront request, where a product with no
           synced rating renders no row at all. Keep Liquid's own markup rather than building
           a second copy here. */
        var placeholder = null;
        if (
          window.Shopify &&
          window.Shopify.designMode &&
          placeholderRow &&
          !content.querySelector('.product-form__rating')
        ) {
          placeholder = placeholderRow.cloneNode(true);
        }

        host.innerHTML = '';
        host.appendChild(content);

        if (placeholder) {
          // Under the title, where a real rating sits.
          var title = content.querySelector('.pdp-title');
          var after = title ? title.parentNode : null;
          if (after && after.parentNode) {
            after.parentNode.insertBefore(placeholder, after.nextSibling);
          } else {
            content.insertBefore(placeholder, content.firstChild);
          }
        }
        syncMedia(host);
        // Before focus moves, so nothing is focused and then disabled under it.
        checkRequested(content, parsed, opts.values);
        applyVariant(content, { initial: true });
        var target = opts.focus ? host.querySelector(opts.focus) : null;
        if (target) {
          target.focus({ preventScroll: true });
        } else {
          focusFirst(host);
        }
      })
      .catch(function () {
        // A superseded request rejects on its own abort, which is not a failure — and
        // navigating here would take the shopper away from the product they just opened.
        if (mine !== seq) return;
        if (keep) current.removeAttribute('aria-busy');
        // Nothing to show and no way to recover in place: send them where the link
        // was always going to.
        window.location.href = url;
      });
  }

  document.addEventListener('quick-shop:open', function (e) {
    var url = e.detail && e.detail.url;
    if (!url || !drawer()) return;
    // Cancelling is what tells the card not to navigate.
    e.preventDefault();
    load(url, e.target);
  });

  /* ---------- Media ---------- */

  function syncMedia(scope) {
    var track = (scope || document).querySelector('[data-pdp-media-track]');
    if (!track) return;
    var root = track.closest('[data-pdp-media]');
    var prev = root.querySelector('[data-pdp-prev]');
    var next = root.querySelector('[data-pdp-next]');
    var fill = root.querySelector('[data-pdp-progress]');
    var max = track.scrollWidth - track.clientWidth;

    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= max - 1;
    if (fill) {
      /* A progress bar, not a handle. The previous version sized the fill to the
         visible fraction and slid it along, which is a scrollbar thumb — it reads as
         something to grab. This grows from the start edge to full width as the track
         reaches its end, so it reports how far through the images you are. */
      /* Seen-fraction, not scroll-fraction: (scrolled + visible) / total. With
         three images two of which are already on screen it starts at 67% and
         finishes at 100%, rather than starting near zero and jumping the lot.
         The old form measured how far the track had travelled, which says
         nothing about how much has been looked at. */
      var seen = track.scrollWidth > 0 ? (track.scrollLeft + track.clientWidth) / track.scrollWidth : 1;
      fill.style.width = Math.min(100, Math.max(4, seen * 100)) + '%';
      fill.style.transform = 'none';
    }
  }

  /* Pointer drag. Scroll-snap gives touch its swipe for free, but a mouse only gets
     the arrows, and the design's gallery is something you pull. `setPointerCapture`
     keeps the gesture alive when the pointer leaves the track, and the distance is
     remembered so the click that ends a drag does not also open the product. */
  var drag = null;

  /* A settle from the previous release is still driving scrollLeft. Stop it before
     reading startLeft, or the glide keeps writing the number this drag is setting and
     the two fight for the ~380ms they overlap. */
  function cancelGlide(track) {
    if (track.__pdpGlide) {
      cancelAnimationFrame(track.__pdpGlide);
      track.__pdpGlide = null;
    }
  }

  document.addEventListener('pointerdown', function (e) {
    var track = e.target.closest ? e.target.closest('[data-pdp-media-track]') : null;
    if (!track || e.button !== 0) return;
    cancelGlide(track);
    drag = { track: track, startX: e.clientX, startLeft: track.scrollLeft, moved: 0 };
    track.setPointerCapture(e.pointerId);
    track.classList.add('is-dragging');
  });

  document.addEventListener('pointermove', function (e) {
    if (!drag) return;
    var dx = e.clientX - drag.startX;
    drag.moved = Math.max(drag.moved, Math.abs(dx));
    drag.lastX = e.clientX;
    drag.track.scrollLeft = drag.startLeft - dx;
  });

  /* One eased run to a known scrollLeft. easeOutCubic, ~380ms: long enough to read
     as deceleration, short enough not to feel like it is catching up. The
     is-dragging class stays on for the duration, because that is what holds
     scroll-snap off while the number is being driven by hand. */
  function glide(track, to) {
    cancelGlide(track);
    var from = track.scrollLeft;
    var delta = to - from;
    if (reducedMotion() || Math.abs(delta) < 1) {
      track.scrollLeft = to;
      track.classList.remove('is-dragging');
      return;
    }
    var start = null;
    var step = function (now) {
      /* A new grab owns the track. Leave both the position and the class to it —
         removing is-dragging here would turn snap back on under a live pointer. */
      if (drag && drag.track === track) {
        track.__pdpGlide = null;
        return;
      }
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / 380);
      var eased = 1 - Math.pow(1 - p, 3);
      track.scrollLeft = from + delta * eased;
      if (p < 1) {
        track.__pdpGlide = requestAnimationFrame(step);
        return;
      }
      track.__pdpGlide = null;
      track.classList.remove('is-dragging');
    };
    track.__pdpGlide = requestAnimationFrame(step);
  }

  function endDrag(e) {
    if (!drag) return;
    var track = drag.track;
    if (e && e.pointerId != null && track.hasPointerCapture(e.pointerId)) {
      track.releasePointerCapture(e.pointerId);
    }
    /* Settle onto the nearest slide by animating scrollLeft ourselves rather than
       handing it to `behavior: smooth`. Snap is off during the drag, and turning it
       back on while a native smooth scroll is in flight makes the browser re-target
       mid-flight — which is the jolt at the end of every drag. Driving the number
       directly means one motion, and snap is only restored once it has stopped.

       Momentum first: a fast flick carries to the next slide even if it did not
       travel a full one, which is what a drag is expected to do. */
    if (drag.moved > 4) {
      var slides = track.querySelectorAll('[data-pdp-slide]');
      var velocity = drag.lastX == null ? 0 : drag.startX - drag.lastX;
      var target = track.scrollLeft + Math.max(-160, Math.min(160, velocity * 0.25));

      var nearest = 0;
      var best = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs(slides[i].offsetLeft - track.offsetLeft - target);
        if (d < best) {
          best = d;
          nearest = slides[i].offsetLeft - track.offsetLeft;
        }
      }
      glide(track, nearest);
    } else {
      track.classList.remove('is-dragging');
    }
    var moved = drag.moved;
    drag = null;
    setTimeout(function () {
      track.__pdpJustDragged = moved > 4;
    }, 0);
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  // Images are natively draggable, and a native drag steals the gesture from the
  // pointer events above — the same failure the quick links row had.
  document.addEventListener('dragstart', function (e) {
    if (e.target && e.target.closest && e.target.closest('[data-pdp-media-track]')) {
      e.preventDefault();
    }
  });

  /* One slide per press, not one track width. The track is wider than a slide — 452
     against 251.5 at desktop — so a viewport-sized scroll cleared roughly 1.8 of them
     and the snap took it the rest of the way: every second image was stepped straight
     over, and the last never became a stop at all. Reading the slides' own offsets
     also keeps the step right at both breakpoints and whatever the gap is. */
  function stepMedia(track, direction) {
    var slides = track.querySelectorAll('[data-pdp-slide]');
    if (!slides.length) return;

    var max = track.scrollWidth - track.clientWidth;
    var here = track.scrollLeft;
    // Rects are viewport-relative, so the current scroll has to be added back to get
    // each slide's position within the track's scroll range.
    var trackLeft = track.getBoundingClientRect().left;
    var target = null;

    for (var i = 0; i < slides.length; i++) {
      var at = slides[i].getBoundingClientRect().left - trackLeft + here;
      // A pixel of tolerance: a snapped position is rarely a whole number.
      if (direction > 0 ? at > here + 1 : at < here - 1) {
        if (direction > 0) { target = at; break; }
        target = at;
      }
    }
    if (target === null) return;

    /* Clamped, so the last slide is reachable. Its own start sits past the end of the
       scroll range — the track only has room for one and a bit — and scrolling to the
       end is what brings it fully into view. */
    track.scrollTo({
      left: Math.max(0, Math.min(target, max)),
      behavior: reducedMotion() ? 'auto' : 'smooth',
    });
  }

  function scrollToMedia(scope, mediaId, instant) {
    var track = scope.querySelector('[data-pdp-media-track]');
    if (!track || !mediaId) return;
    var slides = track.querySelectorAll('[data-pdp-slide]');
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('data-media-id') !== String(mediaId)) continue;
      var smooth = !instant && !reducedMotion();
      track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      return;
    }
  }

  document.addEventListener(
    'scroll',
    function (e) {
      var el = e.target;
      if (!el || !el.hasAttribute || !el.hasAttribute('data-pdp-media-track')) return;
      syncMedia(el.closest('[data-pdp-media]'));
    },
    true
  );

  /* ---------- Variants ---------- */

  function variants(scope) {
    var tag = scope.querySelector('[data-pdp-variants]');
    if (!tag) return [];
    try {
      return JSON.parse(tag.textContent) || [];
    } catch (err) {
      return [];
    }
  }

  /* The option the picker withheld and the order to try its values in, read off
     `data-pf-auto-option` / `data-pf-auto-priority`. Null when nothing is withheld. */
  function autoConfig(picker) {
    if (picker.__qaAuto !== undefined) return picker.__qaAuto;

    var index = picker.hasAttribute('data-pf-auto-option')
      ? parseInt(picker.getAttribute('data-pf-auto-option'), 10)
      : -1;
    var conf = null;

    if (index >= 0) {
      var raw = (picker.getAttribute('data-pf-auto-priority') || '').split(',');
      var order = [];
      for (var i = 0; i < raw.length; i++) {
        var value = raw[i].trim().toLowerCase();
        if (value) order.push(value);
      }
      conf = { index: index, order: order };
    }

    picker.__qaAuto = conf;
    return conf;
  }

  /* What the cart already holds, per variant, so the resolver does not keep offering a
     value the shopper has already taken every unit of. */
  function cartCounts() {
    var state = window.themeCart && window.themeCart.state();
    var items = (state && state.items) || [];
    var counts = {};
    for (var i = 0; i < items.length; i++) {
      counts[items[i].id] = (counts[items[i].id] || 0) + items[i].quantity;
    }
    return counts;
  }

  // Headroom, not just sellability: a variant on "continue selling" reports available at
  // zero, and would hold the queue at the first value for ever.
  function inStock(variant, counts) {
    if (!variant.available) return false;
    if (!variant.tracked) return true;
    return variant.inventory - ((counts && counts[variant.id]) || 0) > 0;
  }

  /* Whether one more can go in the cart — a different question from inStock(): a variant
     set to keep selling at zero has no units to count but can always be bought. */
  function canAdd(variant, counts) {
    if (!variant.available) return false;
    if (variant.oversell || !variant.tracked) return true;
    return variant.inventory - ((counts && counts[variant.id]) || 0) > 0;
  }

  /* Whether any value of the withheld option still has a unit spare for this selection —
     a size can be in stock while the shopper already holds every one of them. Always true
     when nothing is withheld, so an ordinary product is unaffected. */
  function hasHeadroom(picker, variant, all) {
    var conf = autoConfig(picker);
    if (!conf || !variant) return true;

    var probe = (variant.options || []).slice();
    probe[conf.index] = null;

    var counts = cartCounts();
    for (var v = 0; v < all.length; v++) {
      if (matchesLoose(all[v], probe) && canAdd(all[v], counts)) return true;
    }
    return false;
  }

  function matchesLoose(variant, wanted) {
    for (var j = 0; j < wanted.length; j++) {
      if (wanted[j] != null && variant.options[j] !== wanted[j]) return false;
    }
    return true;
  }

  /* Fills the withheld option in, the same queue assets/product-form.js runs: first
     priority value with stock, then any with stock, then anything sellable, then whatever
     exists so a sold-out size still prices. Leaves the selection alone when nothing is
     withheld, so an ordinary product still matches on the shopper's choices only. */
  function resolveAuto(picker, chosen, all) {
    var conf = autoConfig(picker);
    if (!conf) return chosen;

    var probe = chosen.slice();
    probe[conf.index] = null;

    var pool = [];
    for (var v = 0; v < all.length; v++) {
      if (matchesLoose(all[v], probe)) pool.push(all[v]);
    }
    if (!pool.length) return chosen;

    var counts = cartCounts();
    var picked = null;
    for (var p = 0; p < conf.order.length && !picked; p++) {
      for (var i = 0; i < pool.length && !picked; i++) {
        var value = String(pool[i].options[conf.index] || '').toLowerCase();
        if (value === conf.order[p] && inStock(pool[i], counts)) picked = pool[i];
      }
    }
    for (var s = 0; s < pool.length && !picked; s++) {
      if (inStock(pool[s], counts)) picked = pool[s];
    }
    for (var a = 0; a < pool.length && !picked; a++) {
      if (pool[a].available) picked = pool[a];
    }

    picked = picked || pool[0];
    probe[conf.index] = picked.options[conf.index];
    return probe;
  }

  /* Keyed by `data-pf-option`, the row's position in `variant.options`. The withheld
     option is not rendered, so DOM order and that position are not the same thing. A
     picker that publishes no position falls back to order, which is what it used to be. */
  function selection(scope) {
    var chosen = [];
    var groups = scope.querySelectorAll('[data-option-index]');
    for (var i = 0; i < groups.length; i++) {
      var raw = groups[i].getAttribute('data-pf-option');
      var at = raw == null ? i : parseInt(raw, 10);
      var checked = groups[i].querySelector('[data-option-value]:checked');
      chosen[at] = checked ? checked.value : null;
    }
    // A gap is the withheld option: unanswered, and matched as a wildcard below.
    for (var j = 0; j < chosen.length; j++) {
      if (chosen[j] === undefined) chosen[j] = null;
    }
    return chosen;
  }

  /* The values checked in every row but the clicked one, by row and name — the size, on
     a product with the usual two. Names, not ids: each product in a listing numbers its
     own values, so the sibling's markup carries different ids for the same size. */
  function chosenValues(link) {
    var picker = link.closest('[data-pdp-variant-picker]');
    if (!picker) return null;

    var groups = picker.querySelectorAll('[data-option-index]');
    var values = {};
    for (var g = 0; g < groups.length; g++) {
      if (groups[g].contains(link)) continue;
      var checked = groups[g].querySelector('[data-option-value]:checked');
      if (checked) values[positionOf(groups[g], g)] = checked.value;
    }
    return values;
  }

  /* The markup arrives on a colour and no size, whatever the request asked — Liquid cannot
     see the query. The sizes it named are checked here, before the first pass: `values`
     from the sheet a sibling switch left, or the options of a `?variant=`. */
  function checkRequested(scope, url, values) {
    var picker = scope.querySelector('[data-pdp-variant-picker]');
    if (!picker) return;

    var groups = picker.querySelectorAll('[data-option-index]');
    for (var g = 0; values && g < groups.length; g++) {
      var carried = values[positionOf(groups[g], g)];
      if (carried == null) continue;
      var inputs = groups[g].querySelectorAll('[data-option-value]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].value === carried) inputs[i].checked = true;
      }
    }

    var id = url.searchParams.get('variant');
    if (!id) return;
    var all = variants(picker);
    var wanted = null;
    for (var v = 0; v < all.length && !wanted; v++) {
      if (String(all[v].id) === id) wanted = all[v].options || [];
    }
    if (!wanted) return;

    for (var r = 0; r < groups.length; r++) {
      var at = positionOf(groups[r], r);
      var radios = groups[r].querySelectorAll('[data-option-value]');
      for (var n = 0; n < radios.length; n++) {
        if (radios[n].value === wanted[at]) radios[n].checked = true;
      }
    }
  }

  /* A rendered row with nothing checked is a choice still to make — the size, on every
     arrival — so the checked colour must not resolve a variant on its own. */
  function unanswered(picker) {
    var groups = picker.querySelectorAll('[data-option-index]');
    for (var g = 0; g < groups.length; g++) {
      if (!groups[g].querySelector('[data-option-value]:checked')) return true;
    }
    return false;
  }

  // The rendered rows only. `g` is a DOM index; this is the option position it stands for.
  function positionOf(group, fallback) {
    var raw = group.getAttribute('data-pf-option');
    return raw == null ? fallback : parseInt(raw, 10);
  }

  /* Which option values are still reachable given everything else that is chosen.
     Without this a size stays selectable after a colour change that sold it out, and
     the shopper only finds out when Add to cart refuses.

     Each value is judged against the options *before* its own and never the ones after.
     Colour comes first, so it is judged alone — sold out exactly when every variant of
     it is, which is the rule snippets/product-card.liquid marks the card's swatches by.
     Judging it against the chosen size instead faded every colour that size happened to
     be out of, so a jersey with four colours on the card opened its sheet showing one. */
  function updateAvailability(picker, all) {
    var chosen = selection(picker);
    var groups = picker.querySelectorAll('[data-option-index]');

    for (var g = 0; g < groups.length; g++) {
      var inputs = groups[g].querySelectorAll('[data-option-value]');
      var at = positionOf(groups[g], g);
      for (var i = 0; i < inputs.length; i++) {
        var probe = chosen.slice();
        probe[at] = inputs[i].value;

        var reachable = false;
        for (var v = 0; v < all.length; v++) {
          if (!all[v].available) continue;
          var ok = true;
          for (var j = 0; j <= at; j++) {
            if (probe[j] != null && all[v].options[j] !== probe[j]) ok = false;
          }
          if (ok) {
            reachable = true;
            break;
          }
        }

        var label = picker.querySelector('label[for="' + inputs[i].id + '"]');
        if (label) label.classList.toggle('is-unavailable', !reachable);
        inputs[i].toggleAttribute('data-unavailable', !reachable);
      }
    }
  }

  // `spent`: sellable, but every remaining unit is already in the cart. `choose`: a row is
  // still unanswered, so there is no variant to report on.
  function setSubmitState(scope, buyable, spent, choose) {
    var submit = scope.querySelector('[data-pdp-submit]');
    if (!submit) return;
    var labelEl = submit.querySelector('.button__label');
    submit.disabled = !buyable;
    submit.setAttribute('aria-disabled', buyable ? 'false' : 'true');
    // `disabled` belongs to whichever request is in flight for its duration, so the
    // selection's own answer is kept here where releasing the button can read it.
    submit.setAttribute('data-buyable', buyable ? 'true' : 'false');
    if (labelEl) {
      var label = buyable
        ? submit.getAttribute('data-label-add')
        : choose
          ? submit.getAttribute('data-label-choose') || submit.getAttribute('data-label-sold')
          : spent
            ? submit.getAttribute('data-label-max') || submit.getAttribute('data-label-sold')
            : submit.getAttribute('data-label-sold');
      labelEl.textContent = label || labelEl.textContent;
    }
  }

  /* The checked colour's variants, for a sheet with no variant to point at yet. */
  function colourVariants(picker, all) {
    var group = picker.querySelector('[data-option-index][data-option-swatch]');
    var checked = group && group.querySelector('[data-option-value]:checked');
    if (!checked) return [];
    var at = positionOf(group, 0);
    var mine = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].options[at] === checked.value) mine.push(all[i]);
    }
    return mine;
  }

  /* The colour's range, the line `_pdp-price` opens on: its cheapest variant with the
     largest compare-at at that price, and "From" when its sizes span two prices. */
  function renderRange(scope, mine) {
    var price = scope.querySelector('[data-pdp-price]');
    if (!price || !mine.length) return;
    var cheapest = mine[0];
    var varies = false;
    for (var i = 1; i < mine.length; i++) {
      if (mine[i].price < cheapest.price) {
        cheapest = mine[i];
        varies = true;
      } else if (mine[i].price > cheapest.price) varies = true;
      else if (mine[i].compare_at_price > cheapest.compare_at_price) cheapest = mine[i];
    }
    renderPrice(scope, cheapest, varies);
  }

  /* One line for a variant and for a colour's range — `from` is the prefix. Server-
     formatted figures, so the market's currency and the shop's money format are whatever
     Liquid already decided. One currency code per line: the struck compare-at carries it
     and the sale price goes plain ("From CAD $280 $196") — but only while that struck
     node is actually there. */
  function renderPrice(scope, match, from) {
    var price = scope.querySelector('[data-pdp-price]');
    if (!price) return;
    var now = price.querySelector('.pdp-price__now');
    var compare = price.querySelector('.pdp-price__compare');
    var was = price.querySelector('.pdp-price__was');
    var prefix = price.querySelector('.pdp-price__prefix');

    var onSale = match.compare_at_price > match.price;
    var showWas = onSale && !!compare;
    if (now) {
      now.textContent = showWas && match.price_plain ? match.price_plain : match.price_formatted;
      now.classList.toggle('pdp-price__now--sale', onSale);
    }
    // Only the amount is rewritten: the code is markup and the strike belongs to it
    // alone, so the wrapper is what hides.
    if (was) was.textContent = onSale ? match.compare_at_plain : '';
    if (compare) compare.hidden = !onSale;
    if (prefix) prefix.hidden = !from;
  }

  /* `opts.initial` runs this against the markup as it arrives, because Liquid can only
     answer per option and this answers per combination. The gallery then jumps to the
     image rather than gliding to it: an animated move on arrival reads as the sheet
     opening on the wrong image. */
  function applyVariant(scope, opts) {
    var initial = !!(opts && opts.initial);
    var picker = scope.querySelector('[data-pdp-variant-picker]');
    if (!picker) return;

    var all = variants(picker);
    // Nothing to resolve against, so leave what Liquid rendered rather than refusing
    // a form that works: no data is not the same answer as no such variant.
    if (!all.length) return;

    // The withheld option is answered here, against priority and cart headroom, before
    // the match runs — otherwise the first variant in listing order wins the origin.
    var chosen = resolveAuto(picker, selection(picker), all);

    var match = null;
    var fallback = null;
    for (var i = 0; i < all.length; i++) {
      if (!matchesLoose(all[i], chosen)) continue;
      if (fallback === null) fallback = all[i];
      if (all[i].available) {
        match = all[i];
        break;
      }
    }
    // Every match sold out: keep the first so the sheet reports sold out rather than
    // falling back to a variant the shopper did not choose.
    if (!match) match = fallback;

    // An unchecked row matches every value, which is not a choice: the sheet opens on no
    // size and stays on "Choose a size" until the shopper picks one.
    var choose = unanswered(picker);
    if (choose) match = null;

    // The legend echoes the chosen value, e.g. "Color: Toffee".
    var groups = picker.querySelectorAll('[data-option-index]');
    for (var g = 0; g < groups.length; g++) {
      var idx = groups[g].getAttribute('data-option-index');
      var label = picker.querySelector('[data-option-selected="' + idx + '"]');
      var checkedInput = groups[g].querySelector('[data-option-value]:checked');
      if (label && checkedInput) label.textContent = checkedInput.value;
    }

    updateAvailability(picker, all);

    var ids = scope.querySelectorAll('[data-pdp-variant-id]');
    // Save for later saves the row the sheet is showing, so it tracks the same answer.
    var save = scope.querySelector('[data-pdp-save]');
    // As does the details link: the page opens on the colour and size chosen here.
    var details = scope.querySelector('[data-pdp-details]');
    var productUrl = scope.getAttribute('data-product-url') || '';

    // No variant for this combination, or none chosen yet: the id has to be cleared, or
    // the form keeps posting the last one that matched while the sheet shows the new options.
    // The colour still carries: the details link opens the page on it and the gallery
    // shows it.
    if (!match) {
      for (var m = 0; m < ids.length; m++) ids[m].value = '';
      if (save) save.setAttribute('data-variant-id', '');
      if (details && productUrl) details.setAttribute('href', colourUrl(picker, productUrl));
      setSubmitState(scope, false, false, choose);
      var mine = colourVariants(picker, all);
      renderRange(scope, mine);
      for (var c = 0; c < mine.length; c++) {
        if (!mine[c].media_id) continue;
        scrollToMedia(scope, mine[c].media_id, initial);
        break;
      }
      return;
    }

    for (var k = 0; k < ids.length; k++) ids[k].value = match.id;
    if (save) save.setAttribute('data-variant-id', match.id);
    if (details && productUrl) details.setAttribute('href', productUrl + '?variant=' + match.id);

    // A specific variant is chosen, so the colour's range prefix no longer applies.
    renderPrice(scope, match, false);

    // Sellable is not addable: the last unit may already be in the cart, and `available`
    // cannot see that. Refusing here beats letting the add fail — Shopify's cart errors
    // quote the variant title, which would print the withheld option.
    var spent = match.available && !hasHeadroom(picker, match, all);
    setSubmitState(scope, match.available && !spent, spent);

    if (match.media_id) scrollToMedia(scope, match.media_id, initial);
  }

  /* ---------- Wiring ---------- */

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.hasAttribute || !e.target.hasAttribute('data-option-value')) return;
    var scope = e.target.closest('[data-quick-add]');
    if (scope) applyVariant(scope);
  });

  /* The page on the checked colour and nothing else, for the details link while no size
     is chosen. The bare URL when the colour has no id — Shopify opens on it anyway. */
  function colourUrl(picker, productUrl) {
    var group = picker.querySelector('[data-option-index][data-option-swatch]');
    var checked = group && group.querySelector('[data-option-value]:checked');
    var id = checked && checked.getAttribute('data-pf-value-id');
    if (!id) return productUrl;
    var url = new URL(productUrl, window.location.origin);
    url.searchParams.set('option_values', id);
    return url.pathname + url.search;
  }

  /* The sibling's URL on the sizes chosen now: the clicked value in its own row and the
     checked one in every other, as `option_values`, the way assets/pdp-render.js asks.
     The link's bare URL stands while a row is unanswered or has no ids: it opens the
     sibling on its colour alone. */
  function siblingUrl(link) {
    var picker = link.closest('[data-pdp-variant-picker]');
    var href = link.getAttribute('href');
    if (!picker) return href;

    var groups = picker.querySelectorAll('[data-option-index]');
    var ids = [];
    for (var g = 0; g < groups.length; g++) {
      var pick = groups[g].contains(link) ? link : groups[g].querySelector('[data-option-value]:checked');
      var id = pick && pick.getAttribute('data-pf-value-id');
      if (!id) return href;
      ids.push(id);
    }
    if (!ids.length) return href;

    var url = new URL(href, window.location.origin);
    url.searchParams.delete('variant');
    url.searchParams.set('option_values', ids.join(','));
    return url.pathname + url.search;
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    /* A sibling product's colour: the sheet is re-fetched from its link on the size
       chosen here, and focus goes to that colour's own radio in the new markup, where
       the click was. */
    var sibling = e.target.closest('[data-pdp-sibling]');
    if (sibling && sibling.closest('[data-quick-add]')) {
      e.preventDefault();
      load(siblingUrl(sibling), null, {
        keep: true,
        values: chosenValues(sibling),
        focus: '[data-option-index][data-option-swatch] [data-option-value]:checked',
      });
      return;
    }

    var arrow = e.target.closest('[data-pdp-prev], [data-pdp-next]');
    if (arrow) {
      var root = arrow.closest('[data-pdp-media]');
      var track = root && root.querySelector('[data-pdp-media-track]');
      if (track) stepMedia(track, arrow.hasAttribute('data-pdp-next') ? 1 : -1);
      return;
    }

    var save = e.target.closest('[data-pdp-save]');
    if (save) {
      var next = save.getAttribute('aria-pressed') !== 'true';
      // The theme-wide contract: cancelling it takes the control over, and
      // `assets/wishlist.js` does. Uncancelled, the sheet just shows the state.
      var live = save.dispatchEvent(
        new CustomEvent('wishlist:toggle', {
          bubbles: true,
          cancelable: true,
          detail: {
            handle: save.getAttribute('data-product-handle'),
            productId: save.getAttribute('data-product-id'),
            variantId: save.getAttribute('data-variant-id'),
            active: next,
            source: 'quick-add',
          },
        })
      );
      if (live) save.setAttribute('aria-pressed', next ? 'true' : 'false');
    }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.hasAttribute || !form.hasAttribute('data-pdp-form')) return;
    e.preventDefault();

    var scope = form.closest('[data-quick-add]');
    var status = scope ? scope.querySelector('[data-pdp-status]') : null;
    var submit = form.querySelector('[data-pdp-submit]');
    var idField = form.querySelector('[name="id"]');
    var variantId = idField ? Number(idField.value) : 0;

    /* Enter on a radio submits the form whatever the CTA is doing, so every reason to
       refuse is gathered here rather than left to the button: already posting, no
       variant for the current options, or a variant that cannot be bought. */
    if (form.__pdpPending) return;
    if (!variantId || (submit && submit.disabled)) return;
    form.__pdpPending = true;

    // Which product this add belongs to, so a sheet opened later is not closed by it.
    var mine = seq;

    // `is-busy` swaps the label for the spinner in CSS; aria-busy tells assistive tech
    // the same thing. Disabled as well, so a double click cannot post twice.
    if (submit) {
      submit.setAttribute('aria-busy', 'true');
      submit.classList.add('is-busy');
      submit.disabled = true;
    }

    function release() {
      form.__pdpPending = false;
      if (!submit) return;
      submit.removeAttribute('aria-busy');
      submit.classList.remove('is-busy');
      // Never a blanket re-enable: the options can have moved to a sold-out or
      // non-existent variant while this was in flight.
      submit.disabled = submit.getAttribute('data-buyable') === 'false';
    }

    // Added and announced: the sheet has done its job and standing open over the grid
    // only gets in the way of the next add.
    function closeAfterAdd() {
      if (status) status.textContent = form.getAttribute('data-added-label') || '';
      var d = drawer();
      if (!d || !d.open) return release();

      // Released after the close, not on the add: a live button over a drawer still
      // 450ms from closing is a second add.
      closeTimer = setTimeout(function () {
        closeTimer = null;
        // Another product may have opened inside the delay; closing now would shut its
        // sheet instead of the one this add came from.
        if (mine !== seq) return release();
        var closer = d.querySelector('[data-drawer-close]');
        if (closer) closer.click();
        release();
      }, 450);
    }

    /* Through themeCart, so the cart state the resolver reads its headroom from carries
       this add too, and the header and drawer hear `cart:updated` the way they do for
       every other write. A rejected add stays in the sheet: the cart is re-read, the
       withheld option re-resolved against it, and a different variant tried once — a
       cart filled before this page loaded is invisible to the resolver until then. */
    function addThroughThemeCart(id, mayRetry) {
      return window.themeCart.add({ id: id, quantity: 1 }, 'quick-add').then(closeAfterAdd, function (error) {
        var picker = scope && scope.querySelector('[data-pdp-variant-picker]');
        if (!mayRetry || !picker || !autoConfig(picker)) return addFailed(error);
        return window.themeCart
          .refresh('quick-add')
          .then(function () {
            applyVariant(scope);
            var next = idField ? Number(idField.value) : 0;
            var buyable = !submit || submit.getAttribute('data-buyable') !== 'false';
            if (next && next !== id && buyable) {
              // applyVariant re-enabled the button for the new answer; it is still busy.
              if (submit) submit.disabled = true;
              return addThroughThemeCart(next, false);
            }
            addFailed(error);
          })
          .catch(function () {
            addFailed(error);
          });
      });
    }

    // The sheet stays open with the reason: leaving for /cart would drop the shopper on
    // an error page for a choice the sheet can still correct.
    function addFailed(error) {
      if (scope) applyVariant(scope);
      if (status) status.textContent = form.getAttribute('data-error-label') || (error && error.message) || '';
      release();
    }

    if (window.themeCart) {
      addThroughThemeCart(variantId, true);
      return;
    }

    // Without assets/cart.js on the page the sheet posts for itself and announces the
    // result the way themeCart would, so the header count still follows.
    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Add to cart failed: ' + r.status);
        return r.json();
      })
      .then(function (added) {
        return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .catch(function () {
            return null;
          })
          .then(function (cart) {
            var detail = { item: added, cart: cart, itemCount: cart ? cart.item_count : null, source: 'quick-add' };
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: detail, bubbles: true }));
            if (cart) updateCartCount(cart.item_count);
            closeAfterAdd();
          });
      })
      .catch(function () {
        // The form is a real one; letting it post is the honest fallback.
        release();
        form.submit();
      });
  });

  window.addEventListener('resize', function () {
    var roots = document.querySelectorAll('[data-pdp-media]');
    for (var i = 0; i < roots.length; i++) syncMedia(roots[i]);
  });

  /* ---------- Theme editor ---------- */
  /* The sheet is a closed dialog on every page, so there is nothing on screen to select. It
     follows the editor's own selection instead and shows whatever the shell rendered — on a
     product template that is the page's product, which is what the merchant is arranging
     against. No fetch: the sheet is already the section being edited. */
  if (window.Shopify && window.Shopify.designMode) {
    var EDITOR_OPEN = '__quickAddEditorOpen';

    var isOurs = function (e) {
      var d = drawer();
      var t = e.target;
      if (!d || !t || t.nodeType !== 1) return false;
      return t.contains(d) || d.contains(t);
    };

    var editorOpen = function () {
      var d = drawer();
      if (!d || d.open) return;
      window[EDITOR_OPEN] = true;

      /* The shell renders the page's product where there is one — a product template. There
         is none on a collection or in the header group, so the blocks would price at zero
         and `{% form 'product' %}` would refuse a nil product. Fetch the preview instead. */
      var host = body();
      var sheet = host && host.querySelector('[data-quick-add]');
      var onPage = sheet && sheet.getAttribute('data-product-url');
      var preview = d.getAttribute('data-preview-url');

      if (!onPage && preview) {
        load(preview, null);
        return;
      }

      d.show();
    };

    var editorClose = function () {
      var d = drawer();
      if (d && d.open) d.close();
      window[EDITOR_OPEN] = false;
    };

    document.addEventListener('shopify:section:select', function (e) {
      if (isOurs(e)) editorOpen();
    });
    document.addEventListener('shopify:block:select', function (e) {
      if (isOurs(e)) editorOpen();
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (isOurs(e)) editorClose();
    });
    document.addEventListener('shopify:block:deselect', function (e) {
      if (isOurs(e)) editorClose();
    });

    /* A settings change replaces the section with a fresh, closed one. Reopened only if the
       merchant had it open — a re-render while it is not the selection gets no deselect
       afterwards, so an unasked-for sheet would sit over the preview. */
    document.addEventListener('shopify:section:load', function (e) {
      if (isOurs(e) && window[EDITOR_OPEN]) editorOpen();
    });
  }
})();
